import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";

import messages from "../../../assets/locale/messages";
import Input from "../../../components/Input";
import Checkbox from "../../../components/Checkbox";
import Button from "../../../components/Button";
import WarningModal from "./WarningModal";
import {
  addRoleRequest,
  editRoleRequest,
  getEditRoleDataRequest,
  getEditRoleDataResponse,
  getModulesPermissionsRequest,
  getModulesPermissionsResponse
} from "../../../store/Roles/actions";
import { PERMISSIONS_IDS } from "../../../utils/Roles";
import { ROUTE_PATHS } from "../../../utils/RoutesPaths";
import "./AddEditRole.scss";

const AddEditRole = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const lang = useSelector((state) => state.locale.lang);
  const {
    shared,
    roles: { addRole }
  } = messages[lang];
  const modulesArr = useSelector(
    (state) => state.roles.modulesPermissions
  );
  const editRoleData = useSelector(
    (state) => state.roles.editRoleData
  );

  const [selectedPermissionsIds, setSelectedPermissionsIds] =
    useState([]);
  const [permissionsModule, setPremissionsModule] = useState([]);
  const [warningModalOpen, setWarningModalOpen] = useState(false);

  useEffect(() => {
    if (id) {
      dispatch(getEditRoleDataRequest({ id }));
    } else {
      dispatch(getModulesPermissionsRequest());
    }
    return () => {
      dispatch(getModulesPermissionsResponse([]));
      dispatch(getEditRoleDataResponse({}));
    };
  }, [id]);

  useEffect(() => {
    if (modulesArr?.length && !id) {
      setPremissionsModule(
        modulesArr.map((module) => ({
          ...module,
          IsAllChecked: false,
          Permissions: module.Permissions.map((permission) => ({
            ...permission,
            IsChecked: false
          }))
        }))
      );
    }
  }, [modulesArr, id]);

  useEffect(() => {
    const permissionsIdsArr = [];
    permissionsModule.forEach((module) => {
      module.Permissions.forEach((permission) => {
        if (permission.IsChecked) {
          permissionsIdsArr.push(permission.Id);
        }
      });
    });
    setSelectedPermissionsIds(permissionsIdsArr);
  }, [permissionsModule]);

  const {
    values,
    setFieldTouched,
    setFieldValue,
    errors,
    touched,
    handleSubmit,
    dirty,
    isValid,
    setValues
  } = useFormik({
    initialValues: {
      Name: "",
      Description: ""
    },
    validationSchema: Yup.object({
      Name: Yup.string()
        .max(50, "roleNameLength")
        .required("roleNameRequired"),
      Description: Yup.string().max(200, "roleDescriptionLength")
    }),
    onSubmit: async ({ Name, Description }) => {
      if (id) {
        dispatch(
          editRoleRequest({
            data: {
              Id: id,
              Name,
              Description,
              ModulePermissionsIds: [...selectedPermissionsIds]
            },
            navigate
          })
        );
      } else {
        dispatch(
          addRoleRequest({
            data: {
              Name,
              Description,
              ModulePermissionsIds: [...selectedPermissionsIds]
            },
            navigate
          })
        );
      }
    }
  });

  useEffect(() => {
    if (id && editRoleData && Object.keys(editRoleData).length) {
      if (editRoleData?.IsSuperAdmin) {
        navigate(ROUTE_PATHS.roleDetails.replace(":id", id));
      } else {
        const { Name, Description, Modules } = editRoleData;
        setValues({
          ...values,
          Name,
          Description
        });
        setPremissionsModule(
          Modules.map((module) => ({
            ...module,
            Permissions: module.Permissions.map((permission) => ({
              ...permission
            }))
          }))
        );
      }
    }
  }, [id, editRoleData]);

  const handleCheckModule = (isChecked, id) => {
    const permissionsCopy = [...permissionsModule];
    const activeModule = permissionsCopy.find(
      (module) => module.Id === id
    );
    const activeModuleIndex = permissionsCopy.findIndex(
      (module) => module.Id === id
    );
    const updatedModule = {
      ...activeModule,
      IsAllChecked: isChecked,
      Permissions: activeModule.Permissions.map((permission) => ({
        ...permission,
        IsChecked: isChecked
      }))
    };
    permissionsCopy.splice(activeModuleIndex, 1, updatedModule);
    setPremissionsModule(permissionsCopy);
  };

  const handleCheckPermission = (
    isChecked,
    moduleId,
    permissionId
  ) => {
    const modulesCopy = [...permissionsModule];
    const activeModule = modulesCopy.find(
      (module) => module.Id === moduleId
    );
    const activeModuleIndex = modulesCopy.findIndex(
      (module) => module.Id === moduleId
    );

    const permissionsCopy = [...activeModule?.Permissions];
    const activePermission = permissionsCopy?.find(
      (permission) => permission.Id === permissionId
    );
    const activePermissionIndex = permissionsCopy?.findIndex(
      (permission) => permission.Id === permissionId
    );

    if (isChecked) {
      // When check a permission then check it's dependency permissions
      const dependencyPermission = permissionsCopy?.find(
        (permission) =>
          permission.Id === activePermission.DependencyId
      );
      if (dependencyPermission) {
        const dependencyPermissionIndex = permissionsCopy?.findIndex(
          (permission) =>
            permission.Id === activePermission.DependencyId
        );
        permissionsCopy.splice(dependencyPermissionIndex, 1, {
          ...dependencyPermission,
          IsChecked: true
        });
      }
    } else {
      // When uncheck a permission then uncheck any other permission that depends on this one
      permissionsCopy.forEach((permission) => {
        if (permission?.DependencyId === permissionId) {
          permission.IsChecked = false;
        }
      });
    }

    permissionsCopy.splice(activePermissionIndex, 1, {
      ...activePermission,
      IsChecked: isChecked
    });

    const updatedModule = {
      ...activeModule,
      IsAllChecked: permissionsCopy.every(
        (permission) => permission.IsChecked
      ),
      Permissions: [...permissionsCopy]
    };
    modulesCopy.splice(activeModuleIndex, 1, updatedModule);
    setPremissionsModule(modulesCopy);
  };

  const renderPermissionContainer = () => (
    <div className=" bg-white border-radius-10 px-4 py-4 mb-4 ">
      <h6 className="title-color fsize-16 fweight-600 mb-4 d-flex flex-column flex-md-row">
        <span className="mb-2 mb-md-0">
          {addRole.selectPermissions}
        </span>
        <span className="text-light-grey fsize-16 fweight-400 ms-2">{`(${selectedPermissionsIds?.length} ${addRole.permissions})`}</span>
      </h6>
      <div className="permissions-container scroll-within px-3">
        {permissionsModule?.map((module, i) => {
          return (
            <div
              key={`module-${module.Id}`}
              className={`module-container py-2 ${i < permissionsModule?.length - 1 && "light-border-bottom"}`}>
              <div className="row">
                <div className="col-12 col-md-5 col-lg-3">
                  <Checkbox
                    name={`module-${module.Name}`}
                    id={`module-${module.Name}`}
                    label={
                      <span className="text-color fweight-600">
                        {module.Name}
                      </span>
                    }
                    checked={module.IsAllChecked}
                    onChange={(isChecked) =>
                      handleCheckModule(isChecked, module.Id)
                    }
                  />
                </div>
                <div className="col-12 col-md-7 col-lg-9">
                  <div className="row">
                    {module?.Permissions?.map((permission) => {
                      return (
                        <div
                          key={`permission-${permission.Id}`}
                          className={`col-12 col-sm-6 col-lg-4 col-xxl-2 mb-3`}>
                          <Checkbox
                            name={`permission-${permission.Name}`}
                            id={`permission-${permission.Name}`}
                            label={
                              <span className="light-text-color">
                                {permission.Name}
                              </span>
                            }
                            checked={permission.IsChecked}
                            onChange={(isChecked) =>
                              handleCheckPermission(
                                isChecked,
                                module.Id,
                                permission.Id
                              )
                            }
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );

  const renderRoleContainer = () => (
    <form noValidate onSubmit={handleSubmit}>
      <div className=" bg-white border-radius-10 px-4 py-5 mb-4">
        <Input
          required
          inputWrapperClass="mb-4"
          label={addRole.roleName}
          placeholder={addRole.roleNamePlaceholder}
          onChange={(value) => {
            if (!value || value.trim()) {
              const modifiedValue = value.replace(/\s\s+/g, " ");
              if (value[0] !== " ") {
                setFieldTouched("Name");
                setFieldValue("Name", modifiedValue);
              }
            }
          }}
          value={values.Name}
          isInputHasErr={!!(touched.Name && errors.Name)}
          errMsg={errors.Name}
          name="Name"
          id="Name"
        />
        <Input
          required
          label={
            <>
              {addRole.description}
              <span className="text-light-grey ms-1">
                {addRole.optional}
              </span>
            </>
          }
          placeholder={addRole.descriptionPlaceholder}
          onChange={(value) => {
            if (!value || value.trim()) {
              const modifiedValue = value.replace(/\s\s+/g, " ");
              if (value[0] !== " ") {
                setFieldTouched("Description");
                setFieldValue("Description", modifiedValue);
              }
            }
          }}
          value={values.Description}
          isInputHasErr={
            !!(touched.Description && errors.Description)
          }
          errMsg={errors.Description}
          name="Description"
          id="Description"
          rows={4}
          multiline
        />
      </div>
      {id
        ? !!permissionsModule.length && renderPermissionContainer()
        : renderPermissionContainer()}

      <div className="actions-section bg-white border-radius-10 p-4 d-flex justify-content-between align-items-center">
        <Button
          key={shared.cancel}
          outlined
          label={shared.cancel}
          onClick={() => navigate(ROUTE_PATHS.roles)}
          customSpacing={"px-4 px-md-5"}
        />
        <Button
          label={id ? shared.save : shared.submit}
          type={`${
            selectedPermissionsIds.includes(
              PERMISSIONS_IDS.roles_add
            ) ||
            selectedPermissionsIds.includes(
              PERMISSIONS_IDS.roles_edit
            ) ||
            selectedPermissionsIds.includes(
              PERMISSIONS_IDS.roles_delete
            )
              ? "button"
              : "submit"
          }`}
          disabled={
            !dirty || !isValid || !selectedPermissionsIds?.length
          }
          customSpacing={"px-4 px-md-5"}
          onClick={() => {
            if (
              selectedPermissionsIds.includes(
                PERMISSIONS_IDS.roles_add
              ) ||
              selectedPermissionsIds.includes(
                PERMISSIONS_IDS.roles_edit
              ) ||
              selectedPermissionsIds.includes(
                PERMISSIONS_IDS.roles_delete
              )
            ) {
              setWarningModalOpen(true);
            }
          }}
        />
      </div>
    </form>
  );

  return (
    <div className="add-role-container">
      <div className="page-title">
        <h3 className="fweight-700 fsize-22 mb-4 title-color">
          {id ? addRole.editRole : addRole.addRole}
        </h3>
      </div>

      {id
        ? editRoleData &&
          !!Object.keys(editRoleData).length &&
          renderRoleContainer()
        : renderRoleContainer()}

      <WarningModal
        open={warningModalOpen}
        onClose={() => setWarningModalOpen(false)}
        onConfirm={() => {
          setWarningModalOpen(false);
          handleSubmit();
        }}
      />
    </div>
  );
};

export default AddEditRole;
